import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import woozyLogo from "@/assets/woozyai-logo-white-trimmed.png";
import { getFocusableElements, moveFocusWithArrows, trapFocusKeyDown } from "@/lib/focus-management";
import LocalizedLink from "@/components/LocalizedLink";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { CalculateSavingsButton } from "@/components/SavingsCalculator";
import { CalculateCarbonButton } from "@/components/CarbonCalculator";
import { stripLangPrefix, useLanguage } from "@/i18n/LanguageProvider";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { t } = useLanguage();

  const isActive = (path: string) => stripLangPrefix(location.pathname) === path;

  const navLinks = [
    { path: "/how-it-works", label: t("How It Works") },
    { path: "/platform", label: t("Platform") },
    { path: "/solutions", label: t("Solutions") },
    { path: "/integrations", label: t("Integrations") },
    { path: "/project-capital", label: t("Project Capital") },
    { path: "/resources", label: t("Resources") },
  ];

  useEffect(() => {
    if (!isOpen) return;

    getFocusableElements(mobileMenuRef.current)[0]?.focus();
  }, [isOpen]);

  const closeMobileMenu = () => {
    setIsOpen(false);
    requestAnimationFrame(() => menuButtonRef.current?.focus());
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-background/80 shadow-sm backdrop-blur-xl">
      <a href="#main-content" className="skip-link">
        {t("Skip to main content")}
      </a>
      <nav className="mx-auto flex h-[76px] max-w-[1600px] items-center px-4 sm:px-6 xl:px-8" aria-label={t("Primary navigation")}>
        <div className="flex w-full items-center justify-between gap-5">
          {/* Logo */}
          <LocalizedLink to="/" className="group flex shrink-0 items-center rounded-md" aria-label={t("WoozyAI home")}>
            <img 
              src={woozyLogo} 
              alt={t("WoozyAI home")}
              width="128"
              height="41"
              {...({ fetchpriority: "high" } as Record<string, string>)}
              decoding="async"
              className="h-auto w-[112px] transition-[filter] duration-300 group-hover:drop-shadow-[0_0_8px_hsl(var(--primary)/0.4)] sm:w-32" 
            />
          </LocalizedLink>

          {/* Desktop Navigation */}
          <div className="hidden min-w-0 items-center justify-center gap-5 xl:flex 2xl:gap-7">
            {navLinks.map((link) => (
              <LocalizedLink
                key={link.path}
                to={link.path}
                className={cn(
                  "group relative whitespace-nowrap rounded-sm text-sm font-medium transition-colors hover:text-primary",
                  isActive(link.path) ? "text-primary" : "text-muted-foreground"
                )}
                aria-current={isActive(link.path) ? "page" : undefined}
              >
                {link.label}
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute -bottom-1 start-0 h-0.5 bg-gradient-aurora transition-all duration-300",
                    isActive(link.path) ? "w-full" : "w-0 group-hover:w-full"
                  )}
                />
              </LocalizedLink>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden shrink-0 items-center gap-2 xl:flex">
            <div className="flex items-center gap-1 border-e border-border/60 pe-3">
              <CalculateCarbonButton iconOnly />
              <CalculateSavingsButton iconOnly />
            </div>
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" className="hidden 2xl:inline-flex" asChild>
              <LocalizedLink to="/about">{t("About")}</LocalizedLink>
            </Button>
            <Button variant="hero" size="default" className="ms-1" asChild>
              <LocalizedLink to="/contact">{t("Book a Demo")}</LocalizedLink>
            </Button>
          </div>

          {/* Compact navigation */}
          <div className="flex shrink-0 items-center gap-2 xl:hidden">
            <LanguageSwitcher />
            <Button
              ref={menuButtonRef}
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? t("Close primary menu") : t("Open primary menu")}
              aria-controls="mobile-navigation"
              aria-expanded={isOpen}
              type="button"
            >
              {isOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </Button>
          </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div
            id="mobile-navigation"
            ref={mobileMenuRef}
            className="absolute inset-x-0 top-full space-y-2 border-b border-border/60 bg-background/95 px-6 pb-6 pt-4 shadow-lg backdrop-blur-xl xl:hidden"
            role="dialog"
            aria-modal="true"
            aria-label={t("Primary menu")}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                closeMobileMenu();
                return;
              }

              trapFocusKeyDown(event, mobileMenuRef.current);
              moveFocusWithArrows(event, mobileMenuRef.current);
            }}
          >
            {navLinks.map((link) => (
              <LocalizedLink
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
              className={cn(
                  "block rounded-md px-2 py-2 text-base font-medium transition-colors",
                  isActive(link.path) ? "text-primary" : "text-muted-foreground hover:text-primary"
                )}
                aria-current={isActive(link.path) ? "page" : undefined}
              >
                {link.label}
              </LocalizedLink>
            ))}
            <div className="grid gap-3 border-t border-border/50 pt-4 sm:grid-cols-2">
              <Button variant="ghost" className="w-full" asChild>
                <LocalizedLink to="/about" onClick={() => setIsOpen(false)}>
                  {t("About")}
                </LocalizedLink>
              </Button>
              <CalculateSavingsButton fullWidth />
              <CalculateCarbonButton fullWidth />
              <Button variant="hero" className="w-full" asChild>
                <LocalizedLink to="/contact" onClick={() => setIsOpen(false)}>
                  {t("Book a Demo")}
                </LocalizedLink>
              </Button>
            </div>
          </div>
        )}
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
